import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { FaShieldAlt, FaGamepad, FaUsers, FaStar, FaInfoCircle, FaQuestionCircle, FaArrowDown } from 'react-icons/fa';
import { MdLocationOn, MdEmail, MdPhone } from 'react-icons/md';
import pricing_img from '../assets/pricing-carousel.png';
import futuristicpower from '../assets/futuristicfirepower.webp';
import changinbattlegrounds from '../assets/changingbattle.webp';

// Brand palette aligned with GrowthSections/Services
const brand = {
  nearBlack: '#141318',
  darkGray: '#1F2937',
  light: '#E9EAE8',
  white: '#FFFFFF',
  blueGray: '#566D87',
  gold: '#FFD404',
  ink: '#00181C',
};

const About = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  // Hook para animaciones al hacer scroll
  const [heroRef, heroInView] = useInView({ triggerOnce: false, threshold: 0.1 });
  const [whoWeAreRef, whoWeAreInView] = useInView({ triggerOnce: false, threshold: 0.1 });
  const [whatIsLaserTagRef, whatIsLaserTagInView] = useInView({ triggerOnce: false, threshold: 0.1 });
  const [missionRef, missionInView] = useInView({ triggerOnce: false, threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ triggerOnce: false, threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ triggerOnce: false, threshold: 0.1 });
  const [contactRef, contactInView] = useInView({ triggerOnce: false, threshold: 0.1 });

  const testimonials = [
    {
      id: 1,
      name: 'Carlos Martinez',
      role: 'Laser Tag Legend',
      text: 'Mind-blowing laser battles! The tech and arenas are straight out of a sci-fi movie!',
    },
    {
      id: 2,
      name: 'Ana Rodriguez',
      role: 'Event Mastermind',
      text: 'Our team event was an absolute blast! Flawless organization and epic fun!',
    },
    {
      id: 3,
      name: 'David Gomez',
      role: 'Super Dad',
      text: 'My kids are obsessed! Safe, thrilling, and they beg to return every weekend!',
    },
  ];

  return (
    <div className="min-h-screen font-inter" style={{ backgroundColor: brand.nearBlack, color: brand.light }}>
      <style>{`
        h1, h2, h3, h4 { font-family: 'Audiowide', sans-serif; }
        p, div, span, button { font-family: 'Inter', sans-serif; }
        .darkened-image { filter: brightness(50%); }
        .bg-near-black { background-color: ${brand.nearBlack}; }
        .bg-dark-gray { background-color: ${brand.darkGray}; }
        .bg-mid-gray { background-color: #6A706C; }
        .text-light-gray { color: ${brand.light}; }
        .text-mid-gray { color: #6A706C; }
        .text-blue-gray { color: ${brand.blueGray}; }
        .bg-blue-gray { background-color: ${brand.blueGray}; }
        .hover\:bg-mid-gray:hover { background-color: #6A706C; }
        .border-blue-gray { border-color: ${brand.blueGray}; }
        .transition-all { transition-property: all; }
        .duration-500 { transition-duration: 500ms; }
        .duration-1000 { transition-duration: 1000ms; }
        .ease-out { transition-timing-function: ease-out; }
        .delay-100 { transition-delay: 100ms; }
        .delay-200 { transition-delay: 200ms; }
        .delay-300 { transition-delay: 300ms; }
      `}</style>

      {/* Hero Section */}
      <section ref={heroRef} className="relative h-screen flex flex-col items-center justify-center overflow-hidden">
        <img src={pricing_img} alt="Laser Arena" className="absolute inset-0 w-full h-full object-cover darkened-image z-0" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#141318ee] to-[#14131866] z-10"></div>
        <div className={`relative z-20 text-center px-6 py-16 transition-all duration-1000 ease-out ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-6xl md:text-8xl font-bold mb-6 text-light-gray drop-shadow-lg">
            Lazer Zone Arena
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-blue-gray font-medium">
            Canada's ultimate laser tag destination — where strategy meets excitement!
          </p>
        </div>
        <div className={`relative z-20 mt-10 transition-all duration-1000 ease-out delay-300 ${heroInView ? 'opacity-100' : 'opacity-0'}`}>
          <a href="#who-we-are" className="text-blue-gray text-4xl hover:text-light-gray transition">
            <FaArrowDown />
          </a>
        </div>
      </section>

      {/* Who We Are Section */}
      <section id="who-we-are" className="py-20 px-4" style={{ backgroundColor: brand.darkGray }}>
        <div 
          ref={whoWeAreRef}
          className={`max-w-5xl mx-auto text-center transition-all duration-1000 ease-out ${whoWeAreInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <FaInfoCircle className="text-5xl text-blue-gray mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-6 text-light-gray">Who We Are</h2>
          <p className="text-lg text-mid-gray leading-relaxed">
            At <span className="text-blue-gray font-semibold">Lazer Zone Arena</span>, we're on a mission to ignite your sense of adventure. Located in <strong>Cornwall, Ontario</strong>, our arena combines immersive design, cutting-edge laser gear, and adrenaline-fueled missions to offer a one-of-a-kind experience for all ages. Whether you're planning a birthday, group activity, or just looking to have fun, our friendly team and futuristic environment make every visit unforgettable.
          </p>
        </div>
      </section>

      {/* What is Laser Tag Section */}
      <section className="py-20 px-4" style={{ backgroundColor: brand.nearBlack }}>
        <div 
          ref={whatIsLaserTagRef}
          className={`max-w-5xl mx-auto text-center transition-all duration-1000 ease-out ${whatIsLaserTagInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <FaQuestionCircle className="text-5xl text-blue-gray mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-6 text-light-gray">What is Laser Tag?</h2>
          <p className="text-lg text-mid-gray leading-relaxed">
            Laser tag is a high-energy, interactive game where players gear up with vests and infrared-emitting blasters to score points by tagging opponents. Unlike paintball, laser tag is non-contact, painless, and completely safe—perfect for players of all ages. Dive into glowing arenas filled with sound effects, obstacles, and mission-based gameplay that test your teamwork, agility, and strategy.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4" style={{ backgroundColor: brand.darkGray }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-light-gray">Our Mission</h2>
          <div ref={missionRef} className="grid md:grid-cols-3 gap-12">
            <div className={`text-center transition-all duration-500 ease-out delay-100 ${missionInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <FaGamepad className="text-5xl text-blue-gray mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-4 text-light-gray">Immersive Experience</h3>
              <p className="text-mid-gray">State-of-the-art laser technology and thematic arenas for all ages.</p>
            </div>
            <div className={`text-center transition-all duration-500 ease-out delay-200 ${missionInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <FaShieldAlt className="text-5xl text-blue-gray mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-4 text-light-gray">Safe Fun</h3>
              <p className="text-mid-gray">Safety and fun come first with strict protocols and top-quality gear.</p>
            </div>
            <div className={`text-center transition-all duration-500 ease-out delay-300 ${missionInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <FaUsers className="text-5xl text-blue-gray mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-4 text-light-gray">Community Spirit</h3>
              <p className="text-mid-gray">Ideal for families, parties, corporate events and team-building.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4" style={{ backgroundColor: brand.nearBlack }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-light-gray">What Makes Us Unique</h2>
          <div ref={featuresRef} className="grid md:grid-cols-2 gap-12">
            <div className={`transition-all duration-500 ease-out delay-100 ${featuresInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <FeatureCard 
                title="Futuristic Firepower"
                text="Wield precision lasers with real-time tracking that feels like magic!"
                image={futuristicpower}
              />
            </div>
            <div className={`transition-all duration-500 ease-out delay-200 ${featuresInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <FeatureCard 
                title="Ever-Changing Battlegrounds"
                text="Explore wild, thematic arenas that keep every fight fresh and fierce!"
                image={changinbattlegrounds}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4" style={{ backgroundColor: brand.darkGray }}>
        <div 
          ref={testimonialsRef}
          className={`max-w-4xl mx-auto transition-all duration-1000 ease-out ${testimonialsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <h2 className="text-4xl font-bold text-center mb-16 text-light-gray">What Our Players Say</h2>
          <div className="relative h-96">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id}
                className={`absolute inset-0 transition-opacity duration-500 ${index === activeTestimonial ? 'opacity-100' : 'opacity-0'}`}
              >
                <div className="text-center">
                  <FaStar className="text-yellow-400 text-4xl mx-auto mb-6" />
                  <p className="text-xl italic mb-6 text-light-gray">"{testimonial.text}"</p>
                  <h4 className="text-2xl font-bold text-light-gray">{testimonial.name}</h4>
                  <p className="text-mid-gray">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`h-3 w-3 rounded-full ${index === activeTestimonial ? 'bg-blue-gray' : 'bg-mid-gray'}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4" style={{ backgroundColor: brand.nearBlack }}>
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-16 text-light-gray">Contact Us</h2>
          <div ref={contactRef} className="grid md:grid-cols-3 gap-8">
            <div className={`transition-all duration-500 ease-out delay-100 ${contactInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <ContactInfo 
                icon={<MdLocationOn className="text-4xl text-light-gray" />} 
                title="Address" 
                info="917 Second St W, Cornwall, ON K6J 1H8"
              />
            </div>
            <div className={`transition-all duration-500 ease-out delay-200 ${contactInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <ContactInfo 
                icon={<MdPhone className="text-4xl text-light-gray" />} 
                title="Phone" 
                info="(613) 703-0489"
              />
            </div>
            <div className={`transition-all duration-500 ease-out delay-300 ${contactInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <ContactInfo 
                icon={<MdEmail className="text-4xl text-light-gray" />} 
                title="Email" 
                info="info@lazerzonearena.ca"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const FeatureCard = ({ title, text, image }) => (
  <div className="relative group overflow-hidden rounded-xl">
    <img src={image} alt={title} className="w-full h-96 object-cover transform group-hover:scale-105 transition duration-500" />
    <div
      className="absolute inset-0 flex items-end p-6"
      style={{ background: 'linear-gradient(to top, rgba(20, 19, 24, 0.6), rgba(20, 19, 24, 0.25), transparent)' }}
    >
      <div>
        <h3 className="text-2xl font-bold mb-2 text-light-gray">{title}</h3>
        <p className="text-mid-gray">{text}</p>
      </div>
    </div>
  </div>
);

const ContactInfo = ({ icon, title, info }) => (
  <div
    className="p-6 rounded-lg transition"
    style={{ backgroundColor: 'rgba(31, 41, 55, 0.4)', border: `1px solid ${brand.blueGray}33` }}
  >
    <div className="mb-4 text-light-gray">{icon}</div>
    <h3 className="text-xl font-bold mb-2 text-light-gray">{title}</h3>
    <p className="text-light-gray">{info}</p>
  </div>
);

export default About;